import axios, { AxiosRequestConfig, AxiosError, Method } from "axios"

interface AxiosServerConfig {
    bimPath?: string
    fileId?: string
}

type DoneCallback<T = any> = (data: T) => void
type ErrorCallback = (errorMsg: string) => void

export class AxiosServer {
    private _bimPath: string
    private _fileId: string

    constructor(cfg: AxiosServerConfig = {}) {
        this._bimPath = cfg.bimPath === undefined ? "/bim" : cfg.bimPath
        this._fileId = cfg.fileId === undefined ? "" : cfg.fileId
        console.log(
            `AxiosServer initialized. Using global Axios instance. BIM path prefix: ${this._bimPath}, File ID: ${this._fileId || "None"}`
        )
    }

    private _request<T = any>(config: AxiosRequestConfig, done: DoneCallback<T>, error: ErrorCallback): void {
        const params = {
            ...(config.params || {}),
            ...(this._fileId ? { file_id: this._fileId } : {}),
        }

        const requestConfig: AxiosRequestConfig = {
            ...config,
            params,
            method: config.method as Method,
            headers: {
                ...config.headers,
                'Cache-Control': 'public, max-age=604800' // Cache for 1 week
            }
        }

        axios
            .request<T>(requestConfig)
            .then(response => {
                done(response.data)
            })
            .catch((err: Error | AxiosError) => {
                console.error(`Global Axios request failed for ${config.url}:`, err)
                let errorMsg: string
                if (axios.isAxiosError(err)) {
                    errorMsg = err.response
                        ? `HTTP ${err.response.status} (${err.response.statusText}) for ${err.config?.url}`
                        : `Network error for ${err.config?.url}: ${err.message}`
                } else {
                    errorMsg = err.message
                }
                error(errorMsg || "Request failed with unknown error")
            })
    }

    private _getUrl(relativePath: string): string {
        const path = `${this._bimPath}/${relativePath}`.replace(/\/+/g, "/")
        if (path === "/") return "/"
        return path.startsWith("/") ? path : `/${path}`
    }

    getProjects(done: DoneCallback, error: ErrorCallback): void {
        const url = this._getUrl("/projects/index.json")
        this._request({ url: url, method: "get" }, done, error)
    }

    getProject(projectId: string, done: DoneCallback, error: ErrorCallback): void {
        const url = this._getUrl(`/projects/${projectId}/index.json`)
        this._request({ url: url, method: "get" }, done, error)
    }

    getMetadata(projectId: string, modelId: string, done: DoneCallback, error: ErrorCallback): void {
        const url = this._getUrl(`/projects/${projectId}/models/${modelId}/metadata.json`)
        this._request({ url: url, method: "get" }, done, error)
    }

    getGeometry(projectId: string, modelId: string, done: DoneCallback<ArrayBuffer>, error: ErrorCallback): void {
        const url = this._getUrl(`/projects/${projectId}/models/${modelId}/geometry.xkt`)
        this._request<ArrayBuffer>({ url: url, method: "get", responseType: "arraybuffer" }, done, error)
    }

    getObjectInfo(
        projectId: string,
        modelId: string,
        objectId: string,
        done: DoneCallback,
        error: ErrorCallback
    ): void {
        const url = this._getUrl(`/projects/${projectId}/models/${modelId}/props/${objectId}.json`)
        this._request({ url: url, method: "get" }, done, error)
    }

    getIssues(projectId: string, modelId: string, done: DoneCallback, error: ErrorCallback): void {
        const url = this._getUrl(`/projects/${projectId}/models/${modelId}/issues.json`)
        this._request({ url: url, method: "get" }, done, error)
    }

    getSplitModelManifest(
        projectId: string,
        modelId: string,
        manifestName: string,
        done: DoneCallback,
        error: ErrorCallback
    ): void {
        const url = this._getUrl(`/projects/${projectId}/models/${modelId}/${manifestName}`)
        this._request({ url: url, method: "get" }, done, error)
    }

    getSplitModelMetadata(
        projectId: string,
        modelId: string,
        metadataFileName: string,
        done: DoneCallback,
        error: ErrorCallback
    ): void {
        const url = this._getUrl(`/projects/${projectId}/models/${modelId}/${metadataFileName}`)
        this._request({ url: url, method: "get" }, done, error)
    }

    getSplitModelGeometry(
        projectId: string,
        modelId: string,
        geometryFileName: string,
        done: DoneCallback<ArrayBuffer>,
        error: ErrorCallback
    ): void {
        const url = this._getUrl(`/projects/${projectId}/models/${modelId}/${geometryFileName}`)
        this._request<ArrayBuffer>({ url: url, method: "get", responseType: "arraybuffer" }, done, error)
    }
}
